import { useState, useEffect } from 'react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import { apiClient } from '../../services/api'
import { useToast } from '../shared/Toast'
import { useTranslation } from 'react-i18next'

interface TemplateParam {
  key: string
  description: string
}

interface EmailTemplate {
  key: string
  name: string
  subject: string
  body: string
  params: TemplateParam[]
}

export function EmailTemplateEditor() {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    apiClient.get<{ items: EmailTemplate[] }>('/api/manage/email-templates')
      .then(r => {
        setTemplates(r.items)
        if (r.items.length > 0) {
          setSelected(r.items[0].key)
          setSubject(r.items[0].subject)
          setBody(r.items[0].body)
        }
      })
      .finally(() => setLoading(false))
  }, [])

  function selectTemplate(key: string) {
    const tmpl = templates.find(t => t.key === key)
    if (!tmpl) return
    setSelected(key)
    setSubject(tmpl.subject)
    setBody(tmpl.body)
  }

  async function save() {
    if (!selected) return
    setSaving(true)
    try {
      await apiClient.put('/api/manage/email-templates', { key: selected, subject, body })
      // Update local state
      setTemplates(prev => prev.map(t =>
        t.key === selected ? { ...t, subject, body } : t
      ))
      showToast(t('admin.templates.saved'), 'success')
    } catch {
      showToast(t('admin.templates.saveFailed'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const [testEmail, setTestEmail] = useState('')
  const [sendingTest, setSendingTest] = useState(false)

  async function sendTest() {
    if (!selected || !testEmail) return
    setSendingTest(true)
    try {
      await apiClient.post('/api/manage/email-templates/test', { key: selected, to: testEmail })
      showToast(t('admin.templates.testSent'), 'success')
    } catch {
      showToast(t('admin.templates.testFailed'), 'error')
    } finally {
      setSendingTest(false)
    }
  }

  const current = templates.find(t => t.key === selected)

  if (loading) return <p className="text-brand-400 py-4">{t('admin.loading')}</p>

  return (
    <div className="space-y-4">
      {/* Template selector */}
      <div>
        <label className="block text-sm font-medium text-brand-700 mb-1">
          {t('admin.templates.selectTemplate')}
        </label>
        <select
          value={selected ?? ''}
          onChange={e => selectTemplate(e.target.value)}
          className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500"
        >
          {templates.map(tmpl => (
            <option key={tmpl.key} value={tmpl.key}>{tmpl.name}</option>
          ))}
        </select>
      </div>

      {/* Parameter help */}
      {current && current.params.length > 0 && (
        <div className="bg-brand-50 rounded-lg p-3 text-sm">
          <p className="font-medium text-brand-700 mb-1">{t('admin.templates.paramsTitle')}</p>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            {current.params.map(p => (
              <div key={p.key} className="contents">
                <code className="text-brand-600 bg-white px-1.5 py-0.5 rounded text-xs font-mono">{p.key}</code>
                <span className="text-brand-500">{p.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subject */}
      <div>
        <label className="block text-sm font-medium text-brand-700 mb-1">
          {t('admin.templates.subject')}
        </label>
        <input
          type="text"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500"
        />
      </div>

      {/* WYSIWYG body editor */}
      <div>
        <label className="block text-sm font-medium text-brand-700 mb-1">
          {t('admin.templates.body')}
        </label>
        <ReactQuill
          theme="snow"
          value={body}
          onChange={setBody}
          modules={{
            toolbar: [
              [{ header: [false, 2, 3] }],
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link'],
              ['clean'],
            ],
          }}
        />
      </div>

      {/* Save + Test */}
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="bg-brand-500 text-white px-6 py-2 rounded-lg hover:bg-brand-600
            disabled:opacity-50 transition-colors text-sm font-medium"
        >
          {saving ? t('admin.settings.saving') : t('admin.settings.save')}
        </button>

        <div className="flex items-center gap-2 ml-4">
          <input
            type="email"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder={t('admin.templates.testEmailPlaceholder')}
            className="rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500 w-56"
          />
          <button
            onClick={sendTest}
            disabled={sendingTest || !testEmail || !selected}
            className="bg-brand-100 text-brand-700 px-4 py-2 rounded-lg hover:bg-brand-200
              disabled:opacity-50 transition-colors text-sm font-medium whitespace-nowrap"
          >
            {sendingTest ? t('admin.settings.saving') : t('admin.templates.sendTest')}
          </button>
        </div>
      </div>
    </div>
  )
}
